import React, { useState, useEffect } from 'react';
import { Field, Input, Select, Textarea, Button } from '../../../components/ui/FormComponents.jsx';
import Combobox from '../../../components/ui/Combobox.jsx'; // ✨ Combobox para búsqueda
import { Building2, MapPin, Loader, Search } from '../../../components/icons/index.jsx';
import { consultarPadronAFIP } from '../../../services/clients.js';
import {
  getAfipAutofillResetPatch,
  mergeAfipObservaciones,
  normalizeAfipCliente,
} from '../../../services/clientFiscal.js';
import Swal from 'sweetalert2';

/**
 * GeneralTab Component
 * Form tab for general client information including address and tax info
 * Refactored (Dec 2024) - Compact 12-column grid layout, auto-populated locality code
 * Added AFIP Padrón autocomplete integration
 */
export default function GeneralTab({ 
  data, 
  onChange, 
  errors = {}, 
  provinces = [], 
  localities = [],
  onProvinceChange,
  isLoadingLocalities = false,
  toast, // Para mostrar notificaciones
  afipVerified = false, // ✅ Indica si ya se verificó con AFIP
  setAfipVerified, // ✅ Función para actualizar el estado de verificación
  afipBlocked = false, // ✅ Indica si AFIP marcó INACTIVO y debe bloquearse
  setAfipBlocked // ✅ Función para actualizar el estado de bloqueo
}) {
  const [isLoadingAFIP, setIsLoadingAFIP] = useState(false);
  const [pendingAFIPData, setPendingAFIPData] = useState(null); // Datos de AFIP pendientes de mapeo
  const [showAdvanced, setShowAdvanced] = useState(false);
  const requiresArcaVerification = data.tipoDocumento === 'CUIT';

  /**
   * Efecto para mapear localidad cuando se cargan las localidades después de AFIP
   */
  useEffect(() => {
    if (pendingAFIPData?.localidad && localities.length > 0 && !isLoadingLocalities) {
      const normalizarTexto = (texto) => {
        return texto
          .toUpperCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
      };
      
      const localidadNormalizada = normalizarTexto(pendingAFIPData.localidad);
      const localidadEncontrada = localities.find(loc => 
        normalizarTexto(loc.nombre || loc.name) === localidadNormalizada ||
        normalizarTexto(loc.nombre || loc.name).includes(localidadNormalizada) ||
        localidadNormalizada.includes(normalizarTexto(loc.nombre || loc.name))
      );
      
      if (localidadEncontrada) {
        console.log('✓ Localidad mapeada:', pendingAFIPData.localidad, '→', localidadEncontrada.nombre || localidadEncontrada.name);
        onChange({
          ...data,
          localidad: localidadEncontrada.id,
          codigoLocalidad: localidadEncontrada.codigo || localidadEncontrada.codigoLocalidad || ''
        });
      } else {
        console.warn('⚠ No se pudo mapear localidad:', pendingAFIPData.localidad);
        onChange({
          ...data,
          localidadManual: pendingAFIPData.localidad,
          localidad: '',
          codigoLocalidad: '',
        });
      }
      
      // Limpiar datos pendientes
      setPendingAFIPData(null);
    }
  }, [data, isLoadingLocalities, localities, onChange, pendingAFIPData]);

  /**
   * Handle input changes and propagate to parent
   */
  const handleChange = (field, value) => {
    if (field === 'cuit' || field === 'tipoDocumento') {
      // La verificación corresponde al documento exacto; no debe invalidarse
      // cuando ARCA normaliza el formato del CUIT al completar los datos.
      setAfipVerified?.(field === 'tipoDocumento' && value === 'DNI');
      setAfipBlocked?.(false);
    }
    onChange({ ...data, [field]: value });
  };

  /**
   * Handle province change with special logic to reset locality and code
   */
  const handleProvinceChange = (value) => {
    onChange({
      ...data,
      provincia: value,
      localidad: '',
      localidadManual: '',
      codigoLocalidad: '',
    });
    if (onProvinceChange) {
      onProvinceChange(value);
    }
  };

  /**
   * Handle locality change and auto-populate locality code
   */
  const handleLocalityChange = (localityId) => {
    const selectedLocality = localities.find(loc => loc.id === localityId);
    onChange({
      ...data,
      localidad: localityId,
      localidadManual: '',
      codigoLocalidad: selectedLocality?.codigo || '',
    });
  };

  /**
   * Validate CUIT format (Argentina)
   * Format: XX-XXXXXXXX-X
   */
  // Validar documento: acepta CUIT (11) o DNI (7-8)
  const isValidDocumento = (raw) => {
    const d = (raw || '').replace(/\D/g, '');
    return d.length === 11 || d.length === 8 || d.length === 7;
  };

  /**
   * Autocompletar desde AFIP Padrón A13
   */
  const handleAutocompletarAFIP = async () => {
  const docDigits = (data.cuit || '').replace(/\D/g, '');

  if (!docDigits) {
    toast?.error?.('Ingrese CUIT o DNI primero');
    return;
  }

  if (!(docDigits.length === 11 || docDigits.length === 8 || docDigits.length === 7)) {
    toast?.error?.('Documento inválido: CUIT 11 dígitos, DNI 7-8 dígitos');
    return;
  }

  setIsLoadingAFIP(true);
  onChange({
    ...data,
    ...getAfipAutofillResetPatch(),
  });

  try {
    const datosAFIP = await consultarPadronAFIP(docDigits, true);

    if (!datosAFIP || datosAFIP.ok === false) {
      toast?.error?.(datosAFIP?.msg || 'Error al consultar AFIP');
      if (setAfipVerified) setAfipVerified(false);
      return;
    }

    /* =========================================================
       🚫 BLOQUEO OBLIGATORIO – CONTRIBUYENTE INACTIVO
       ========================================================= */
    if (
      datosAFIP.estadoContribuyente &&
      datosAFIP.estadoContribuyente.toUpperCase() === 'INACTIVO'
    ) {
      // Mostrar SweetAlert con nombre y CUIT y NO completar campos
      const nombre = datosAFIP.razonSocial || datosAFIP.nombre || '';
      const cuit = datosAFIP.cuit || '';

      const mensajeHtml = `El contribuyente figura <strong>INACTIVO</strong> en AFIP.<br/>${nombre ? 'Nombre: <strong>' + nombre + '</strong><br/>' : ''}${cuit ? 'CUIT: <strong>' + cuit + '</strong><br/>' : ''}<br/>No se completaron los campos del formulario.`;

      // Mostrar alerta modal para que el usuario la confirme
      await Swal.fire({
        icon: 'error',
        title: 'Contribuyente INACTIVO',
        html: mensajeHtml,
        confirmButtonText: 'Entendido',
      });

      // No tocar los campos del formulario (el usuario quería que no se complete nada)
      if (setAfipVerified) setAfipVerified(false);
      if (setAfipBlocked) setAfipBlocked(true);
      return;
    }

    /* =========================================================
       📍 DOMICILIO – leer desde persona_raw.domicilio[]
       ========================================================= */
    const domicilios = datosAFIP.persona_raw?.domicilio || [];

    const domicilio =
      domicilios.find(d => d.tipoDomicilio === 'FISCAL') ||
      domicilios.find(d => d.tipoDomicilio === 'LEGAL/REAL') ||
      domicilios[0] ||
      null;

    const provinciaAFIP = domicilio?.descripcionProvincia || '';
    const localidadAFIP = domicilio?.localidad || '';
    const codigoPostalAFIP = domicilio?.codigoPostal || '';
    const direccionAFIP = domicilio?.direccion || '';
    const calleAFIP = domicilio?.calle || '';
    const numeroAFIP = domicilio?.numero || '';
  // Piso / Departamento pueden venir en campos separados
  const pisoAFIP = domicilio?.piso || domicilio?.domicilioPiso || '';
  const dptoAFIP = domicilio?.oficinaDptoLocal || domicilio?.oficina || domicilio?.oficinaDpto || domicilio?.dpto || domicilio?.oficinaDptoLocal || '';

    const normalizar = (t) =>
      (t || '')
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    /* =========================================================
       🗺️ MAPEO DE PROVINCIA
       ========================================================= */
    let provinciaId = data.provincia;

    if (provinciaAFIP && provinces.length > 0) {
      const provNorm = normalizar(provinciaAFIP);
      const prov = provinces.find(p =>
        normalizar(p.nombre || p.name) === provNorm ||
        normalizar(p.nombre || p.name).includes(provNorm) ||
        provNorm.includes(normalizar(p.nombre || p.name))
      );
      if (prov) {
        provinciaId = prov.id;
      }
    }

    /* =========================================================
       🏘️ MAPEO DE LOCALIDAD (si ya están cargadas)
       ========================================================= */
    let localidadId = data.localidad;
    let codigoLocalidad = data.codigoLocalidad;

    if (localidadAFIP && localities.length > 0) {
      const locNorm = normalizar(localidadAFIP);
      const loc = localities.find(l =>
        normalizar(l.nombre || l.name) === locNorm ||
        normalizar(l.nombre || l.name).includes(locNorm) ||
        locNorm.includes(normalizar(l.nombre || l.name))
      );
      if (loc) {
        localidadId = loc.id;
        codigoLocalidad = loc.codigo || loc.codigoLocalidad || '';
      }
    }

    /* =========================================================
       🧾 AUTOCOMPLETAR DATOS
       ========================================================= */
    // Preferir calle+numero para la dirección principal cuando estén disponibles
    const direccionCalculada = (calleAFIP && numeroAFIP) ? `${calleAFIP} ${numeroAFIP}`.trim() : direccionAFIP;

    const normalizedAfipData = normalizeAfipCliente(datosAFIP, data);
    const newData = {
      ...data,
      ...normalizedAfipData,
      // Evitamos dejar el Piso/Dpto pegado a la dirección principal.
      direccion: direccionCalculada || direccionAFIP || data.direccion,
      codigoPostal: codigoPostalAFIP,
      provincia: provinciaId,
      localidad: localidadId,
      codigoLocalidad: codigoLocalidad,
      // Mapear piso/dpto en campos separados (y mantener el campo combinando piso+depto para compatibilidad)
      domicilioCalle: calleAFIP || data.domicilioCalle,
      domicilioNumero: numeroAFIP || data.domicilioNumero,
      domicilioPiso: pisoAFIP || data.domicilioPiso,
      domicilioDpto: dptoAFIP || data.domicilioDpto,
      pisoDepto: (pisoAFIP || dptoAFIP) ? [pisoAFIP ? 'Piso ' + pisoAFIP : '', dptoAFIP ? 'Dpto ' + dptoAFIP : ''].filter(Boolean).join(' ').trim() : data.pisoDepto,
      observaciones: mergeAfipObservaciones(data.observaciones, datosAFIP),
    };

    onChange(newData);

    /* =========================================================
       🔁 DISPARAR CARGA DE LOCALIDADES SI CAMBIÓ PROVINCIA
       ========================================================= */
    if (provinciaId && provinciaId !== data.provincia && onProvinceChange) {
      onProvinceChange(provinciaId);

      if (localidadAFIP) {
        setPendingAFIPData({ localidad: localidadAFIP });
      }
    }

    if (setAfipVerified) setAfipVerified(true); 
  if (setAfipBlocked) setAfipBlocked(false);

    if (!normalizedAfipData.condicionIVA) {
      toast?.warning?.('AFIP no devolvió condición IVA. Completala manualmente antes de facturar.');
    } else {
    toast?.success?.('✓ Contribuyente verificado correctamente con ARCA');
    }
  } catch (error) {
    console.error('Error AFIP:', error);
    toast?.error?.('Error al consultar AFIP');
    if (setAfipVerified) setAfipVerified(false);
      if (setAfipBlocked) setAfipBlocked(false);
  } finally {
    setIsLoadingAFIP(false);
  }
};



  return (
    <div className="space-y-6">
      {/* 🔒 Bloqueo ARCA cuando el contribuyente está INACTIVO */}
      {afipBlocked && (
        <div className="bg-red-100 border-l-4 border-red-600 p-4 rounded">
          <div className="flex items-start gap-3">
            <span className="text-red-700 text-2xl">❌</span>
            <div>
              <h4 className="text-red-800 font-semibold mb-1">Contribuyente INACTIVO — Bloqueado</h4>
              <p className="text-red-700 text-sm">
                El contribuyente figura <strong>INACTIVO</strong> en ARCA y no se puede continuar con la creación.
                {data.razonSocial || data.nombre ? (
                  <><br />Nombre: <strong>{data.razonSocial || data.nombre}</strong></>
                ) : null}
                {data.cuit ? (
                  <><br />CUIT: <strong>{data.cuit}</strong></>
                ) : null}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* ⚠️ Advertencia ARCA obligatoria únicamente para CUIT */}
      {requiresArcaVerification && !afipVerified && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-start gap-3">
            <span className="text-red-600 text-2xl">⚠️</span>
            <div>
              <h4 className="text-red-800 font-semibold mb-1">
                Verificación ARCA Obligatoria
              </h4>
              <p className="text-red-700 text-sm">
                Debe ingresar el <strong>CUIT</strong> y presionar el botón{' '}
                <strong>"⚠️ Verificar ARCA"</strong> para validar que el contribuyente existe en ARCA.
                No se podrá guardar el cliente sin esta verificación.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* ✅ Confirmación de verificación */}
      {requiresArcaVerification && afipVerified && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
          <div className="flex items-start gap-3">
            <span className="text-green-600 text-2xl">✓</span>
            <div>
              <h4 className="text-green-800 font-semibold mb-1">
                Cliente Verificado con ARCA
              </h4>
              <p className="text-green-700 text-sm">
                Los datos del contribuyente fueron validados exitosamente con ARCA.
                Puede continuar completando el formulario.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Información General */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Building2 size={20} className="text-[rgb(var(--brand))]" />
          <h3 className="text-lg font-medium">Información General</h3>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Tipo de Documento - 4 columnas */}
          <div className="col-span-12 md:col-span-4">
            <Field label="Tipo de Documento" required error={errors.tipoDocumento}>
              <Select
                value={data.tipoDocumento || ''}
                onChange={(e) => handleChange('tipoDocumento', e.target.value)}
              >
                <option value="">Seleccionar...</option>
                <option value="CUIT">CUIT</option>
                <option value="DNI">DNI</option>
                <option value="Pasaporte">Pasaporte</option>
              </Select>
            </Field>
          </div>

          {/* Número de Documento (CUIT/DNI) - 4 columnas */}
          <div className="col-span-12 md:col-span-4">
            <Field 
              label={data.tipoDocumento === 'CUIT' ? 'CUIT' : data.tipoDocumento === 'DNI' ? 'DNI' : 'Número de Documento'} 
              required 
              error={errors.cuit}
            >
              <div className="flex gap-2">
                <Input
                  value={data.cuit || ''}
                  onChange={(e) => handleChange('cuit', e.target.value)}
                  placeholder={data.tipoDocumento === 'CUIT' ? '20-12345678-9' : data.tipoDocumento === 'DNI' ? '12345678' : 'Número'}
                  inputMode="numeric"
                  className="flex-1"
                />
                
                 {/* Indicador de verificación ARCA */}
                 {requiresArcaVerification && afipVerified && (
                  <span className="text-green-600 text-sm font-medium flex items-center gap-1 px-2">
                    ✓ Verificado
                  </span>
                )}
                
                {requiresArcaVerification && <Button
                  type="button"
                  onClick={handleAutocompletarAFIP}
                  disabled={isLoadingAFIP || !isValidDocumento(data.cuit)}
                  title={afipVerified ? "Ya verificado - Click para actualizar desde ARCA" : "⚠️ OBLIGATORIO: Verificar con ARCA antes de guardar"}
                  className={`px-3 py-2 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 ${
                    afipVerified 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700 animate-pulse'
                  }`}
                >
                  {isLoadingAFIP ? (
                    <Loader className="animate-spin h-4 w-4" />
                  ) : (
                    <Search size={16} />
                  )}
                  <span className="hidden sm:inline">
                    {afipVerified ? 'Actualizar' : '⚠️ Verificar ARCA'}
                  </span>
                </Button>}
              </div>
            </Field>
          </div>

          {/* Condición IVA - 4 columnas */}
          <div className="col-span-12 md:col-span-4">
            <Field label="Condición IVA" required error={errors.condicionIVA}>
              <Select
                value={data.condicionIVA || ''}
                onChange={(e) => handleChange('condicionIVA', e.target.value)}
              >
                <option value="">Seleccionar...</option>
                <option value="Responsable Inscripto">Responsable Inscripto</option>
                <option value="Monotributista">Monotributista</option>
                <option value="Exento">Exento</option>
                <option value="Consumidor Final">Consumidor Final</option>
              </Select>
            </Field>
          </div>

          {/* Razón Social - 8 columnas */}
          <div className="col-span-12 md:col-span-8">
            <Field label="Razón Social" required error={errors.razonSocial}>
              <Input
                value={data.razonSocial || ''}
                onChange={(e) => handleChange('razonSocial', e.target.value)}
                placeholder="Empresa S.A."
                autoComplete="organization"
              />
            </Field>
          </div>

          {/* Nombre de Fantasía - 4 columnas */}
          <div className="col-span-12 md:col-span-4">
            <Field label="Nombre de Fantasía">
              <Input
                value={data.nombreFantasia || ''}
                onChange={(e) => handleChange('nombreFantasia', e.target.value)}
                placeholder="Nombre comercial"
              />
            </Field>
          </div>

          {/* ✨ ELIMINADO: Categoría Cliente (se asigna EVENTUAL por defecto en backend) */}
        </div>
      </div>

      {/* Dirección */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <MapPin size={20} className="text-[rgb(var(--brand))]" /> 
          <h3 className="text-lg font-medium">Dirección</h3>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* ✨ REORGANIZADO: Dirección (6 col) + Piso/Dpto (3 col) + Código Postal (3 col) en misma fila */}
          
          {/* Dirección - 6 columnas */}
          <div className="col-span-12 md:col-span-6">
            <Field label="Dirección">
              <Input
                value={data.direccion || ''}
                onChange={(e) => handleChange('direccion', e.target.value)}
                placeholder="Av. Corrientes 1234"
                autoComplete="address-line1"
              />
            </Field>
          </div>

          {/* Piso / Departamento - 3 columnas */}
          <div className="col-span-12 md:col-span-3">
            <Field label="Piso / Depto">
              <Input
                value={data.pisoDepto || ''}
                onChange={(e) => handleChange('pisoDepto', e.target.value)}
                placeholder="5° B"
                autoComplete="address-line2"
              />
            </Field>
          </div>

          {/* Código Postal - 3 columnas (movido aquí) */}
          <div className="col-span-12 md:col-span-3">
            <Field label="Código Postal">
              <Input
                value={data.codigoPostal || ''}
                onChange={(e) => handleChange('codigoPostal', e.target.value)}
                placeholder="1043"
                maxLength={8}
              />
            </Field>
          </div>

          {/* Provincia - 4 columnas (✨ Combobox con búsqueda) */}
          <div className="col-span-12 md:col-span-4">
            <Field label="Provincia">
              <Combobox
                options={provinces.map(prov => ({ value: prov.id, label: prov.name }))}
                value={data.provincia || ''}
                onChange={(value) => handleProvinceChange(value)}
                placeholder="Buscar provincia..."
              />
            </Field>
          </div>

          {/* Localidad - 4 columnas (✨ Combobox con búsqueda) */}
          <div className="col-span-12 md:col-span-4">
            <Field label="Localidad">
              <div className="relative">
                <Combobox
                  options={localities.map(loc => ({ value: loc.id, label: loc.name }))}
                  value={data.localidad || ''}
                  onChange={(value) => handleLocalityChange(value)}
                  disabled={!data.provincia || isLoadingLocalities}
                  placeholder={isLoadingLocalities ? 'Cargando...' : 'Buscar localidad...'}
                />
                {isLoadingLocalities && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                    <Loader className="animate-spin h-4 w-4 text-[rgb(var(--muted))]" />
                  </div>
                )}
              </div>
            </Field>
          </div>

          {/* Código de Localidad - 4 columnas (READ ONLY) */}
          <div className="col-span-12 md:col-span-4">
            <Field label="Código de Localidad" hint="Se completa automáticamente">
              <Input
                value={data.codigoLocalidad || ''}
                readOnly
                disabled
                placeholder="Automático"
                className="bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </Field>
          </div>

          {/* ✨ Código Postal movido arriba a la fila de Dirección */}
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced(prev => !prev)}
          className="text-sm font-medium text-[rgb(var(--brand))] hover:underline"
        >
          {showAdvanced ? 'Ocultar' : 'Mostrar'} datos avanzados
        </button>

        {showAdvanced ? (
          <div className="mt-3 grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <Field label="Latitud">
                <Input
                  value={data.lat || ''}
                  onChange={(e) => handleChange('lat', e.target.value)}
                  placeholder="-34.6037"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Longitud">
                <Input
                  value={data.lng || ''}
                  onChange={(e) => handleChange('lng', e.target.value)}
                  placeholder="-58.3816"
                />
              </Field>
            </div>
          </div>
        ) : null}
      </div>

      {/* Observaciones */}
      <div>
        <Field label="Observaciones">
          <Textarea
            value={data.observaciones || ''}
            onChange={(e) => handleChange('observaciones', e.target.value)}
            placeholder="Notas adicionales sobre el cliente..."
            rows={3}
          />
        </Field>
      </div>
    </div>
  );
}
